import { Fieldset } from '@components/Fields/Fieldset';
import { VerticalGrid } from '@components/VerticalGrid';
import useFormatter from '@hooks/useFormatter';
import { Box, makeStyles, Typography } from '@material-ui/core';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import CommentIcon from '@material-ui/icons/Comment';
import ImportContactsIcon from '@material-ui/icons/ImportContacts';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import RecordVoiceOverIcon from '@material-ui/icons/RecordVoiceOver';
import SettingsPhoneIcon from '@material-ui/icons/SettingsPhone';
import { ICliente } from '@model/Cliente';
import { EnumClienteTipo, IComboClienteTipo } from '@model/ClienteTipo';
import { IComboTipoCobrancaChip, IContratoCliente } from '@model/ContratoCliente';
import { IComboContratoOperadora } from '@model/ContratoOperadora';
import ILinha from '@model/Linha';
import React from 'react';

export type ResumoInfoProps = {
    linha: Partial<ILinha>
    cliente: Partial<ICliente>
    contrato: Partial<IContratoCliente>
    contratoOperadoraCombo: IComboContratoOperadora
    clienteTipoCombo: IComboClienteTipo
    tipoCobrancaChipCombo: IComboTipoCobrancaChip
}

export function ResumoInfo ({ linha, cliente, contrato, tipoCobrancaChipCombo, clienteTipoCombo }: ResumoInfoProps) {
    const formatter = useFormatter();

    const getBooleanString = React.useCallback((value?: string) => {
        return value === "1" ? "Sim" : "Não";
    }, []);

    const clienteTipo = React.useMemo(() => clienteTipoCombo.set.find(x => x.clienteTipoId === cliente.clienteTipoId), [clienteTipoCombo.set, cliente.clienteTipoId]);
    const tipoCobrancaChip = React.useMemo(() => tipoCobrancaChipCombo.set.find(x => x.value === contrato.tipoCobrancaChip), [tipoCobrancaChipCombo.set, contrato.tipoCobrancaChip]);

    return (
        <VerticalGrid>
            <Fieldset
                icon={<SettingsPhoneIcon />}
                title="Linha"
            >
                <Field name="Número" displayValue={formatter.toCelular(linha.numeroSemMascara)} />
                <Field name="Contrato Operadora" displayValue={`${linha?.contratoOperadoraEmpresaNome} - ${linha?.contratoOperadoraOperadoraNome}`} />
                <Field name="Linha Empresa" displayValue={getBooleanString(linha.linhaEmpresa)} />
                <Field name="Voz Ilimitada" displayValue={getBooleanString(linha.vozIlimitada)} />
            </Fieldset>

            <Fieldset 
                icon={<AssignmentIndIcon />} 
                title="Cliente - Identificação"
            >
                <Field name="Tipo de Pessoa" displayValue={clienteTipo?.nome} />
                <Field name={cliente.clienteTipoId === EnumClienteTipo.PessoaJuridica ? "CNPJ" : "CPF"} displayValue={cliente.cpf} />
                <Field name="Nome Completo" displayValue={cliente.nome} />

                {
                    cliente.clienteTipoId === EnumClienteTipo.PessoaFisica && (
                        <Field name="Identidade" displayValue={cliente.rg} />
                    )
                }

                <Field name="SPC" displayValue={getBooleanString(cliente.spc)} />
            </Fieldset>

            <Fieldset 
                icon={<ImportContactsIcon />} 
                title="Cliente - Contato" 
            >
                <Field name="Local de Trabalho" displayValue={cliente.localTrabalho} />
                <Field name="E-mail" displayValue={cliente.email} />
                <Field name="Celular" displayValue={cliente.celular} />
                <Field name="Remetente Whatsapp" displayValue={cliente.whatsappEnvio} />
                <Field name="Telefone Alternativo" displayValue={cliente.telefoneAlternativo} />
                <Field name="Telefone Alternativo 2" displayValue={cliente.telefoneAlternativo2} />
                <Field name="Telefone Alternativo 3" displayValue={cliente.telefoneAlternativo3} />
                <Field name="Telefone Alternativo 4" displayValue={cliente.telefoneAlternativo4} />
            </Fieldset>

            <Fieldset 
                icon={<LocationOnIcon />} 
                title="Cliente - Endereço" 
            >
                <Field name="Logradouro" displayValue={cliente.enderecoLogradouro} />
                <Field name="Número" displayValue={cliente.enderecoNumero} />
                <Field name="Complemento" displayValue={cliente.enderecoComplemento} />
                <Field name="Bairro" displayValue={cliente.enderecoBairro} />
                <Field name="Cidade" displayValue={cliente.enderecoCidade} />
                <Field name="UF" displayValue={cliente.enderecoUf} />
                <Field name="Referência" displayValue={cliente.enderecoReferencia} />
                <Field name="CEP" displayValue={cliente.enderecoCep} />
            </Fieldset>
            
            <Fieldset 
                icon={<RecordVoiceOverIcon />} 
                title="Cliente - Indicação"
            >
                <Field name="Indicado Por" displayValue={cliente.indicadoPor} />
                <Field name="Telefone Indicação" displayValue={cliente.telefoneIndicacao} />
            </Fieldset>
            
            {
                !!cliente.observacao && (
                    <Fieldset 
                        icon={<CommentIcon />} 
                        title="Cliente - Observação"
                    >
                        <Field name="Observação" displayValue={cliente.observacao}></Field>
                    </Fieldset>
                )
            }
            
            <Fieldset 
                icon={<SettingsPhoneIcon />} 
                title="Contrato" 
            >
                <Field name="Início" displayValue={formatter.toDate(contrato.inicio)}></Field>
                <Field name="Valor Plano" displayValue={formatter.toFloat(contrato.valorPlano)}></Field>
                <Field name="Valor Consultoria" displayValue={formatter.toFloat(contrato.valorAssessoria)}></Field>
                <Field name="Franquia GB" displayValue={formatter.toFloat(contrato.franquiaGb)}></Field>
                <Field name="Valor Chip" displayValue={formatter.toFloat(contrato.valorChip)}></Field>
                <Field name="Tipo Cobrança Chip" displayValue={tipoCobrancaChip?.description}></Field>
                <Field name="Cobrar Inteira" displayValue={formatter.toBooleanString(contrato.cobrarInteira)}></Field>
                <Field name="Usuário da Linha" displayValue={contrato.usuarioLinha}></Field>
                <Field name="Vendedor" displayValue={contrato.vendedorNome}></Field>
            </Fieldset>
        </VerticalGrid>
    )
}

type FieldProps = {
    name: string,
    displayValue?: string | null
}

function Field ({name, displayValue}: FieldProps) {
    const cls = useStyles();

    // <Box width="25%"></Box>
    // <Box></Box>


    return (
        <Box display="flex">
            <Box className={cls.fieldName}><Typography variant="caption"><strong>{name}</strong>:</Typography></Box>
            <Box><Typography variant="caption">{displayValue !== null && displayValue !== undefined ? displayValue : ""}</Typography></Box>
        </Box>
    )
}

const useStyles = makeStyles(theme => ({
    fieldName: {
        textAlign: 'right',
        width: 250,
        marginRight: theme.spacing(2)
    }
}));